'use client';

// useContentSources - Unified state management for all content sources
// Uses TanStack Query for server state management

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ContentSource,
  ContentSourceSummary,
  ContentSourcesResponse,
  DeleteContentResponse,
  ReorderContentResponse,
} from '../../types/content-source';

const CONTENT_SOURCES_KEY = 'contentSources';

interface UseContentSourcesOptions {
  /** Bot ID to fetch content for */
  botId: string | null;
  /** Whether to enable the query */
  enabled?: boolean;
}

interface UseContentSourcesReturn {
  /** List of content sources */
  sources: ContentSource[];
  /** Summary of content sources */
  summary: ContentSourceSummary | null;
  /** Whether data is loading */
  isLoading: boolean;
  /** Whether data is being fetched */
  isFetching: boolean;
  /** Error if fetch failed */
  error: Error | null;
  /** Total character count across all sources */
  totalCharacters: number;
  /** Whether any source is still processing */
  isProcessing: boolean;
  /** Whether all sources are ready */
  allReady: boolean;
  /** Whether any source has an error */
  hasErrors: boolean;
  /** Delete a content source */
  deleteSource: (contentId: string) => Promise<void>;
  /** Reorder content sources */
  reorderSources: (orderedIds: string[]) => Promise<void>;
  /** Whether delete mutation is pending */
  isDeleting: boolean;
  /** Whether reorder mutation is pending */
  isReordering: boolean;
  /** Refetch content sources */
  refetch: () => Promise<void>;
}

/**
 * Fetches content sources from the API
 */
async function fetchContentSources(botId: string): Promise<ContentSourcesResponse> {
  const response = await fetch(`/api/bots/${botId}/content`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.error?.message || 'Failed to fetch content sources');
  }

  return response.json();
}

/**
 * Deletes a content source
 */
async function deleteContentSource(
  botId: string,
  contentId: string
): Promise<DeleteContentResponse> {
  const response = await fetch(`/api/bots/${botId}/content/${contentId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.error?.message || 'Failed to delete content');
  }

  return response.json();
}

/**
 * Reorders content sources
 */
async function reorderContentSources(
  botId: string,
  orderedIds: string[]
): Promise<ReorderContentResponse> {
  const response = await fetch(`/api/bots/${botId}/content/reorder`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ orderedIds }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.error?.message || 'Failed to reorder content');
  }

  return response.json();
}

/**
 * Hook for managing content sources with TanStack Query
 *
 * @example
 * ```tsx
 * const {
 *   sources,
 *   totalCharacters,
 *   isLoading,
 *   deleteSource,
 *   reorderSources,
 * } = useContentSources({ botId: 'abc-123' });
 * ```
 */
export function useContentSources({
  botId,
  enabled = true,
}: UseContentSourcesOptions): UseContentSourcesReturn {
  const queryClient = useQueryClient();

  // Query for fetching content sources
  const query = useQuery({
    queryKey: [CONTENT_SOURCES_KEY, botId],
    queryFn: () => fetchContentSources(botId!),
    enabled: enabled && !!botId,
    staleTime: 30_000, // 30 seconds
  });

  // Mutation for deleting a source
  const deleteMutation = useMutation({
    mutationFn: (contentId: string) => deleteContentSource(botId!, contentId),
    onMutate: async (contentId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [CONTENT_SOURCES_KEY, botId] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<ContentSourcesResponse>([
        CONTENT_SOURCES_KEY,
        botId,
      ]);

      // Optimistically remove the source
      if (previousData) {
        const updatedSources = previousData.data.sources.filter(
          (s) => s.id !== contentId
        );
        const updatedSummary = calculateSummary(updatedSources);

        queryClient.setQueryData<ContentSourcesResponse>(
          [CONTENT_SOURCES_KEY, botId],
          {
            data: {
              sources: updatedSources,
              summary: updatedSummary,
            },
          }
        );
      }

      return { previousData };
    },
    onError: (_error, _contentId, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(
          [CONTENT_SOURCES_KEY, botId],
          context.previousData
        );
      }
    },
    onSettled: () => {
      // Refetch after mutation
      queryClient.invalidateQueries({ queryKey: [CONTENT_SOURCES_KEY, botId] });
    },
  });

  // Mutation for reordering sources
  const reorderMutation = useMutation({
    mutationFn: (orderedIds: string[]) => reorderContentSources(botId!, orderedIds),
    onMutate: async (orderedIds) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [CONTENT_SOURCES_KEY, botId] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<ContentSourcesResponse>([
        CONTENT_SOURCES_KEY,
        botId,
      ]);

      // Optimistically update the order
      if (previousData) {
        const sourceMap = new Map(
          previousData.data.sources.map((s) => [s.id, s])
        );

        const reorderedSources = orderedIds
          .map((id, index) => {
            const source = sourceMap.get(id);
            if (source) {
              return { ...source, displayOrder: index };
            }
            return null;
          })
          .filter((s): s is ContentSource => s !== null);

        queryClient.setQueryData<ContentSourcesResponse>(
          [CONTENT_SOURCES_KEY, botId],
          {
            data: {
              sources: reorderedSources,
              summary: previousData.data.summary,
            },
          }
        );
      }

      return { previousData };
    },
    onError: (_error, _orderedIds, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(
          [CONTENT_SOURCES_KEY, botId],
          context.previousData
        );
      }
    },
    onSettled: () => {
      // Refetch after mutation
      queryClient.invalidateQueries({ queryKey: [CONTENT_SOURCES_KEY, botId] });
    },
  });

  // Extract data from query
  const sources = query.data?.data.sources ?? [];
  const summary = query.data?.data.summary ?? null;

  // Calculate derived state
  const totalCharacters = summary?.totalCharacters ?? 0;
  const isProcessing = sources.some((s) => s.status === 'processing');
  const allReady = sources.length > 0 && sources.every((s) => s.status === 'ready');
  const hasErrors = sources.some((s) => s.status === 'error');

  return {
    sources,
    summary,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    totalCharacters,
    isProcessing,
    allReady,
    hasErrors,
    deleteSource: async (contentId: string) => {
      await deleteMutation.mutateAsync(contentId);
    },
    reorderSources: async (orderedIds: string[]) => {
      await reorderMutation.mutateAsync(orderedIds);
    },
    isDeleting: deleteMutation.isPending,
    isReordering: reorderMutation.isPending,
    refetch: async () => {
      await query.refetch();
    },
  };
}

/**
 * Calculate summary from sources
 */
function calculateSummary(sources: ContentSource[]): ContentSourceSummary {
  const byType: Record<string, number> = {
    text: 0,
    file: 0,
    image: 0,
    url: 0,
    google_sheets: 0,
    google_docs: 0,
  };

  let totalCharacters = 0;

  for (const source of sources) {
    byType[source.type] = (byType[source.type] || 0) + 1;
    totalCharacters += source.characterCount;
  }

  return {
    totalSources: sources.length,
    totalCharacters,
    byType: byType as ContentSourceSummary['byType'],
  };
}

export type { UseContentSourcesReturn };
